// ---------------------------------------------------------------------------
// YouAreYou (uru) — EnvBanner
//
// Renders a small, fixed banner at the top of the viewport in non-production
// environments so that developers and testers always know which environment
// they are looking at.
//
// - Production: renders nothing
// - Staging: orange banner — "STAGING ENVIRONMENT"
// - Development: blue banner — "LOCAL DEVELOPMENT"
//
// The banner is dismissible per session (stays hidden until page reload).
// It includes the current API base URL for quick verification.
// ---------------------------------------------------------------------------

import { useState } from "react";

const APP_ENV = import.meta.env.VITE_APP_ENV ?? "development";
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "unknown";

interface BannerConfig {
  label: string;
  bgClass: string;
  textClass: string;
  dotClass: string;
}

const BANNER_CONFIG: Record<string, BannerConfig> = {
  staging: {
    label: "STAGING ENVIRONMENT",
    bgClass: "bg-amber-500/90",
    textClass: "text-amber-950",
    dotClass: "bg-amber-950/60",
  },
  development: {
    label: "LOCAL DEVELOPMENT",
    bgClass: "bg-sky-500/90",
    textClass: "text-sky-950",
    dotClass: "bg-sky-950/60",
  },
};

export default function EnvBanner() {
  const [dismissed, setDismissed] = useState(false);

  // Never render in production
  if (APP_ENV === "production") return null;

  // Allow dismissal for the current session
  if (dismissed) return null;

  const config = BANNER_CONFIG[APP_ENV] ?? BANNER_CONFIG.development;

  return (
    <div
      className={`fixed inset-x-0 top-0 z-[9999] flex items-center justify-center gap-3 px-4 py-1.5 text-xs font-semibold backdrop-blur-sm ${config.bgClass} ${config.textClass}`}
      role="status"
      aria-label={`Current environment: ${APP_ENV}`}
    >
      {/* Pulsing dot */}
      <span className="relative flex h-2 w-2 shrink-0">
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${config.dotClass}`}
        />
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${config.dotClass}`}
        />
      </span>

      {/* Label */}
      <span className="tracking-wider">{config.label}</span>

      {/* API indicator */}
      <span className="hidden opacity-60 sm:inline">
        — API: {API_BASE.replace(/^https?:\/\//, "")}
      </span>

      {/* Dismiss button */}
      <button
        onClick={() => setDismissed(true)}
        className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium opacity-70 transition-opacity hover:opacity-100 ${config.textClass}`}
        aria-label="Dismiss environment banner"
      >
        ✕
      </button>
    </div>
  );
}
